const INFLECTION = /^(ing|ed|er|est|ly|s|es|tion|ness|able|ment|ful)s?$/i;

/** Join Gemini partials without turning "Thank"+"Thankyou" into one smash. */
export function mergeTranscriptPiece(prev, text) {
  const a = String(prev || '').trim();
  const b = String(text || '').trim();
  if (!b) return a;
  if (!a) return b;
  if (a === b) return a;
  if (b.startsWith(a)) {
    const suffix = b.slice(a.length);
    if (!suffix) return b;
    if (/\s/.test(b)) return b;
    if (!/[A-Za-z]$/.test(a) || !/^[A-Za-z]/.test(suffix)) return b;
    if (INFLECTION.test(suffix)) return b;
    return `${a} ${suffix}`;
  }
  if (a.startsWith(b)) return a;
  return `${a} ${b}`;
}

export function endsSentence(text) {
  return /[.!?]'?"?$/.test(String(text || '').trim());
}

export function looksTokenFragment(text) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  return words.length > 0 && words.length <= 6 && !endsSentence(text);
}

export function collapseTurns(turns) {
  const out = [];
  for (const t of turns || []) {
    const text = String(t.text || '').trim();
    if (!text) continue;
    const speaker = t.speaker || 'caller';
    const last = out[out.length - 1];
    if (last && last.speaker === speaker) {
      const prev = String(last.text || '').trim();
      if (text === prev) continue;
      if (text.startsWith(prev) || prev.startsWith(text)) {
        last.text = mergeTranscriptPiece(prev, text);
      } else if (endsSentence(prev) && !looksTokenFragment(text)) {
        out.push({ speaker, text });
      } else {
        last.text = mergeTranscriptPiece(prev, text);
      }
    } else {
      out.push({ speaker, text });
    }
  }
  return out;
}

export function sentenceBlocks(text) {
  let t = String(text || '').trim();
  if (!t) return [];
  t = t.replace(/([.!?])([A-Za-z“"])/g, '$1 $2');
  t = t.replace(/—/g, ' — ');
  t = t.replace(/\s+/g, ' ').trim();
  return t.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);
}

export function parseLabeledDialogue(text) {
  const raw = String(text || '');
  const re = /(Rosie|Caller)\s*:\s*/gi;
  const matches = [...raw.matchAll(re)];
  if (!matches.length) return [];
  const out = [];
  for (let i = 0; i < matches.length; i += 1) {
    const who = /rosie/i.test(matches[i][1]) ? 'Rosie' : 'Caller';
    const start = matches[i].index + matches[i][0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index : raw.length;
    const body = raw.slice(start, end).trim();
    if (!body) continue;
    out.push({
      speaker: who === 'Rosie' ? 'assistant' : 'caller',
      who,
      text: body,
    });
  }
  return out;
}

export function dialogueTurns({ transcript, summary } = {}) {
  const collapsed = collapseTurns(transcript);
  if (collapsed.length) {
    const byWho = [];
    for (const t of collapsed) {
      let who = /assistant|ai|rosie/i.test(t.speaker || '') ? 'Rosie' : 'Caller';
      if (/^(you|owner|me|user)$/i.test(t.speaker || '')) who = 'You';
      const last = byWho[byWho.length - 1];
      if (last && last.who === who) {
        last.text = `${last.text} ${t.text}`;
      } else {
        byWho.push({ who, text: t.text });
      }
    }
    return byWho
      .map((t) => ({ who: t.who, sentences: sentenceBlocks(t.text) }))
      .filter((t) => t.sentences.length);
  }
  const labeled = parseLabeledDialogue(summary);
  if (labeled.length) {
    return labeled.map((t) => ({ who: t.who, sentences: sentenceBlocks(t.text) }));
  }
  const paras = String(summary || '').split(/\n+/).map((s) => s.trim()).filter(Boolean);
  if (!paras.length) return [];
  return [{ who: null, sentences: paras.flatMap(sentenceBlocks) }];
}

export function dialogueToPlainText({ transcript, summary } = {}) {
  const turns = dialogueTurns({ transcript, summary });
  const lines = [];
  for (const turn of turns) {
    const text = (turn.sentences || []).join(' ').trim();
    if (!text) continue;
    if (turn.who) {
      lines.push(`${turn.who}: ${text}`);
    } else {
      lines.push(text);
    }
  }
  return lines.join('\n');
}
